function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function mapSessionSignalPatchFromRepositoryState(state) {
  if (state?.kind !== "ready") {
    return null;
  }
  return {
    _map_session: {
      view: cloneJson(state.view),
      selection: isPlainObject(state.selectionTarget)
        ? { detailsTarget: cloneJson(state.selectionTarget) }
        : {},
    },
  };
}

export function mapSessionPageShowRequiresReload(previousState, nextState) {
  if (
    nextState?.kind === "conflict"
    || nextState?.kind === "unavailable"
    || nextState?.kind === "invalid"
    || nextState?.kind === "unsupportedVersion"
  ) {
    return false;
  }
  if (nextState?.kind === "missing") {
    return previousState?.kind === "ready";
  }
  if (nextState?.kind !== "ready") {
    return false;
  }
  if (
    previousState?.kind === "conflict"
    && previousState.pending?.revision === nextState.revision
  ) {
    return false;
  }
  if (previousState?.kind === "ready") {
    return previousState.revision !== nextState.revision;
  }
  return true;
}

export function createMapSessionLiveController({
  repository,
  eventTarget = globalThis.window ?? null,
  readCurrentView,
  reload = () => globalThis.location?.reload?.(),
  reportError = (error) => {
    if (typeof globalThis.reportError === "function") {
      globalThis.reportError(error);
      return;
    }
    console.error("Fishy map session command failed", error);
  },
} = {}) {
  if (
    !repository
    || typeof repository.state !== "function"
    || typeof repository.subscribe !== "function"
    || typeof repository.recordView !== "function"
    || typeof repository.recordSelectionTarget !== "function"
  ) {
    throw new TypeError("Map session live controller requires a repository.");
  }
  if (typeof readCurrentView !== "function") {
    throw new TypeError("Map session live controller requires a narrow view reader.");
  }
  if (typeof reload !== "function" || typeof reportError !== "function") {
    throw new TypeError("Map session live controller lifecycle callbacks must be functions.");
  }

  let lastRepositoryState = repository.state();
  let unsubscribe = null;
  let started = false;
  let disposed = false;
  let reportedAcknowledgementFailure = "";
  let commandSequence = 0;

  function acknowledgementFailure(state) {
    if (state?.kind === "ready") {
      reportedAcknowledgementFailure = "";
      return null;
    }
    const kind = typeof state?.kind === "string" ? state.kind : "unknown";
    const operation = typeof state?.operation === "string" ? state.operation : "record";
    const reason = typeof state?.reason === "string" ? state.reason : "typed-state-failure";
    const signature = `${kind}:${operation}:${reason}`;
    if (signature === reportedAcknowledgementFailure) {
      return null;
    }
    reportedAcknowledgementFailure = signature;
    return new Error(
      `Map session ${operation} acknowledgement returned ${kind}: ${reason}`,
    );
  }

  function observeCommand(run) {
    const sequence = commandSequence + 1;
    commandSequence = sequence;
    try {
      Promise.resolve(run()).then((state) => {
        if (disposed || sequence !== commandSequence) {
          return;
        }
        const error = acknowledgementFailure(state);
        if (error) {
          reportError(error);
        }
      }).catch((error) => {
        if (!disposed && sequence === commandSequence) {
          reportError(error);
        }
      });
    } catch (error) {
      reportError(error);
    }
  }

  function recordViewSnapshot(snapshot) {
    observeCommand(() => repository.recordView(snapshot?.view));
    return true;
  }

  function recordSelectionSnapshot(selection) {
    const currentView = readCurrentView();
    observeCommand(() => repository.recordSelectionTarget(
      isPlainObject(selection?.detailsTarget)
        ? cloneJson(selection.detailsTarget)
        : null,
      { currentView },
    ));
    return true;
  }

  function handleRepositoryState(nextState, change) {
    const previousState = lastRepositoryState;
    lastRepositoryState = nextState;
    if (change?.cause !== "pageshow") {
      return;
    }
    const conflictError = nextState?.kind === "conflict"
      ? acknowledgementFailure(nextState)
      : null;
    if (conflictError) {
      reportError(conflictError);
    }
    if (mapSessionPageShowRequiresReload(previousState, nextState)) {
      reload();
    }
  }

  function handlePageHide(event) {
    if (event?.persisted !== true) {
      dispose();
    }
  }

  function start() {
    if (started || disposed) {
      return false;
    }
    started = true;
    unsubscribe = repository.subscribe(handleRepositoryState);
    eventTarget?.addEventListener?.("pagehide", handlePageHide);
    return true;
  }

  function dispose() {
    if (disposed) {
      return;
    }
    disposed = true;
    unsubscribe?.();
    unsubscribe = null;
    eventTarget?.removeEventListener?.("pagehide", handlePageHide);
  }

  return Object.freeze({
    dispose,
    initialSignalPatch() {
      return mapSessionSignalPatchFromRepositoryState(repository.state());
    },
    recordSelectionSnapshot,
    recordViewSnapshot,
    start,
  });
}
